import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ActionFeedbackContext = createContext(null);

const DEFAULT_DURATION_MS = 4500;

let toastId = 0;

export function ActionFeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    ({ type = 'info', message, durationMs = DEFAULT_DURATION_MS } = {}) => {
      const trimmed = String(message || '').trim();
      if (!trimmed) return null;

      const id = ++toastId;
      setToasts((prev) => [...prev.slice(-4), { id, type, message: trimmed }]);

      const timer = setTimeout(() => dismissToast(id), durationMs);
      timersRef.current.set(id, timer);
      return id;
    },
    [dismissToast]
  );

  const value = useMemo(() => ({ pushToast, dismissToast }), [pushToast, dismissToast]);

  return (
    <ActionFeedbackContext.Provider value={value}>
      {children}
      <ActionToastStack toasts={toasts} onDismiss={dismissToast} />
    </ActionFeedbackContext.Provider>
  );
}

export function useActionFeedback() {
  const ctx = useContext(ActionFeedbackContext);
  if (!ctx) {
    return {
      pushToast: () => null,
      dismissToast: () => {},
    };
  }
  return ctx;
}

function ActionToastStack({ toasts, onDismiss }) {
  if (!toasts.length) return null;

  return (
    <div
      className="app-action-toast-stack"
      role="region"
      aria-label="Action feedback"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`app-action-toast app-action-toast--${toast.type}`}
          role="status"
        >
          <span className="app-action-toast__message app-readable">{toast.message}</span>
          <button
            type="button"
            className="app-action-toast__dismiss"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(toast.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
